import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { listChannels } from '../../api/channels';
import { contactsExportUrl } from '../../api/contacts';
import { FUNNEL_STAGE_LABELS, FUNNEL_STAGES, type ContactDto, type LostReason } from '../../api/types';
import type { BadgeChannelType } from '../dialogs/ChannelBadge';
import { usePatchContact } from '../../hooks/use-contacts';
import { useKanbanColumns, useKanbanOptimisticMove } from '../../hooks/use-kanban';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';
import { KanbanColumn } from './KanbanColumn';
import { KanbanContactPanel } from './KanbanContactPanel';
import { LostReasonModal } from './LostReasonModal';

function allColumnIds(columns: Record<string, ContactDto[]>): string[] {
  return FUNNEL_STAGES.flatMap((stage) => (columns[stage] ?? []).map((c) => c.id));
}

export function KanbanBoard() {
  const { columns, isLoading, isError } = useKanbanColumns();
  const kanbanMove = useKanbanOptimisticMove();
  const patch = usePatchContact();
  const toast = useToast();
  const draggingRef = useRef<{ contact: ContactDto; fromStage: string } | null>(null);
  const [pendingLost, setPendingLost] = useState<{ contact: ContactDto; fromStage: string } | null>(null);
  const [selected, setSelected] = useState<ContactDto | null>(null);

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const channelTypeById = useMemo(() => {
    const map: Record<string, BadgeChannelType> = {};
    for (const c of channels.data ?? []) map[c.id] = c.type;
    return map;
  }, [channels.data]);

  function handleDragStart(e: React.DragEvent, contact: ContactDto) {
    draggingRef.current = { contact, fromStage: contact.funnelStage };
    e.dataTransfer.effectAllowed = 'move';
  }

  function commitMove(contact: ContactDto, fromStage: string, toStage: string, lostReason?: LostReason) {
    if (fromStage === toStage) return;
    const rollback = kanbanMove.move(contact, fromStage, toStage);
    patch.mutate(
      { id: contact.id, body: { funnel_stage: toStage, lost_reason: lostReason ?? null } },
      {
        onError: (err) => {
          rollback();
          toast.push(err instanceof Error ? err.message : 'Не удалось перенести контакт');
        },
      },
    );
  }

  function handleDrop(toStage: string) {
    const dragging = draggingRef.current;
    draggingRef.current = null;
    if (!dragging) return;
    const { contact, fromStage } = dragging;
    if (fromStage === toStage) return;
    if (toStage === 'lost') {
      setPendingLost({ contact, fromStage });
      return;
    }
    commitMove(contact, fromStage, toStage);
  }

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto p-4">
        {FUNNEL_STAGES.map((s) => (
          <Skeleton key={s} className="h-96 w-64 shrink-0" />
        ))}
      </div>
    );
  }
  if (isError) return <div className="p-4 text-sm text-red-600">Не удалось загрузить воронку.</div>;

  // U4: export exactly the cards on the board — the board has no filter bar of its own (it
  // always shows all funnel stages), so "visible cards" means the ids currently loaded into
  // each column, passed as `ids` to the same /contacts/export the contacts table uses. That
  // also makes the export match what's on screen even if a column is capped by KANBAN_LIMIT.
  const visibleIds = allColumnIds(columns);

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-end gap-2 border-b border-border px-4 py-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={visibleIds.length === 0}
            onClick={() => (window.location.href = contactsExportUrl('csv', {}, visibleIds))}
          >
            Экспорт CSV
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={visibleIds.length === 0}
            onClick={() => (window.location.href = contactsExportUrl('xlsx', {}, visibleIds))}
          >
            Экспорт XLSX
          </Button>
        </div>
        <div className="flex min-h-0 flex-1">
          <div className="flex flex-1 gap-3 overflow-x-auto p-4">
            {FUNNEL_STAGES.map((stage) => (
              <KanbanColumn
                key={stage}
                stage={stage}
                label={FUNNEL_STAGE_LABELS[stage]}
                contacts={columns[stage] ?? []}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                onCardClick={setSelected}
                channelTypeById={channelTypeById}
              />
            ))}
          </div>
          {selected && <KanbanContactPanel contact={selected} onClose={() => setSelected(null)} />}
        </div>
      </div>
      <LostReasonModal
        open={Boolean(pendingLost)}
        onCancel={() => setPendingLost(null)}
        onConfirm={(reason) => {
          if (pendingLost) commitMove(pendingLost.contact, pendingLost.fromStage, 'lost', reason);
          setPendingLost(null);
        }}
      />
    </>
  );
}
